import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Bell, CheckCircle, XCircle, AlertCircle, MessageCircle, ChevronLeft, User as UserIcon, ChevronRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { TrainerMemberService } from '../lib/trainerMemberService';
import { displayNameFromProfile } from '../pages/account/accountShared';
import { ChatService } from '../lib/chatService';
import { notifyTrainerOfInviteAccepted } from '../lib/notifyTrainerOfInviteAccepted';
import {
  getNotifications,
  markAllAsRead,
  markAsRead,
  subscribeToNewNotifications,
  type MemberNotification,
} from '../lib/memberNotificationService';
import type { TrainerMemberRelationship, User } from '../types/dataModel';
import { useAuth } from '../contexts/AuthContext';
import { CHAT_NAV_ENABLED } from '../lib/chatNavEnabled';
import { PAGE_CHROME_LOADER_BRAND_GREEN, PAGE_CHROME_OFF_WHITE } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';
import { LoadingSpinner } from './LoadingSpinner';

type PendingRequest = TrainerMemberRelationship & { trainer: User };

/** Theme colors for avatar placeholders – one solid color per avatar (hash-based) */
const THEME_AVATAR_COLORS = ['#367BF4', 'var(--action-primary)', '#EB3F34', '#F7D046'] as const;
const LIGHT_ICON_COLORS = ['#fff', 'var(--ink)', '#fff', 'var(--ink)'] as const; /* green and yellow need dark type */

function getAvatarStyle(userId: string): React.CSSProperties {
  let n = 0;
  for (let i = 0; i < userId.length; i++) n += userId.charCodeAt(i);
  const idx = Math.abs(n) % THEME_AVATAR_COLORS.length;
  return {
    background: THEME_AVATAR_COLORS[idx],
    color: LIGHT_ICON_COLORS[idx],
  };
}
const SLIDER_TRAINERS = [
  { id: 1, name: 'Sarah Johnson', specialty: 'Weight Loss' },
  { id: 2, name: 'Mike Rodriguez', specialty: 'Strength' },
  { id: 3, name: 'Emily Chen', specialty: 'Yoga & Pilates' },
  { id: 4, name: 'David Thompson', specialty: 'Cardio' },
  { id: 5, name: 'Jessica Martinez', specialty: 'Rehabilitation' },
  { id: 6, name: 'Alex Kim', specialty: 'Functional' },
];

export const Notifications: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [trainerUpdates, setTrainerUpdates] = useState<MemberNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [acceptedTrainer, setAcceptedTrainer] = useState<{ id: string; name: string } | null>(null);
  const [last7DisplayCount, setLast7DisplayCount] = useState(5);
  const [olderDisplayCount, setOlderDisplayCount] = useState(5);
  const INITIAL_DISPLAY_COUNT = 5;

  usePageStatusBar({
    background: loading ? PAGE_CHROME_LOADER_BRAND_GREEN : PAGE_CHROME_OFF_WHITE,
  });
  /** True after we've been mounted for a short time; avoids marking read on React Strict Mode's fake unmount. */
  const didReallyMountRef = useRef(false);

  const { last7Days, older } = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    d.setHours(0, 0, 0, 0);
    const sevenDaysAgo = d.getTime();
    const last7: MemberNotification[] = [];
    const old: MemberNotification[] = [];
    for (const n of trainerUpdates) {
      const t = n.createdAt ? new Date(n.createdAt).getTime() : 0;
      if (t >= sevenDaysAgo) last7.push(n);
      else old.push(n);
    }
    return { last7Days: last7, older: old };
  }, [trainerUpdates]);

  const loadAll = useCallback(async () => {
    if (!profile?.id) return;
    try {
      setLoading(true);
      setError(null);
      const [requests, updates] = await Promise.all([
        TrainerMemberService.getPendingRequests(profile.id),
        getNotifications(profile.id),
      ]);
      setPendingRequests(requests);
      setTrainerUpdates(updates);
    } catch (err: any) {
      console.error('Error loading notifications:', err);
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  // Set flag as soon as we mount so dashboard always knows we came from here when user navigates back (avoids flash).
  useEffect(() => {
    try { sessionStorage.setItem('member_from_notifications_page', '1'); } catch { /* ignore */ }
  }, []);
  // Set didReallyMountRef after a short delay so we don't mark-as-read on Strict Mode's immediate unmount.
  useEffect(() => {
    const t = setTimeout(() => { didReallyMountRef.current = true; }, 500);
    return () => clearTimeout(t);
  }, []);

  // When member actually leaves the notifications page (unmount after having been here), mark all as read and notify dashboard to refetch count.
  useEffect(() => {
    return () => {
      if (profile?.id && didReallyMountRef.current) {
        markAllAsRead(profile.id).then(() => {
          window.dispatchEvent(new CustomEvent('member-notifications-marked-read'));
        });
      }
    };
  }, [profile?.id]);

  useEffect(() => {
    if (profile?.id) loadAll();
  }, [profile?.id, loadAll]);

  // When a new notification arrives while user is on this page, refetch the list so it appears without refresh.
  useEffect(() => {
    if (!profile?.id) return;
    const sub = subscribeToNewNotifications(profile.id, () => {
      getNotifications(profile.id).then((updates) => {
        setTrainerUpdates(updates);
      }).catch(() => {});
    });
    return () => sub.unsubscribe();
  }, [profile?.id]);

  const loadPendingRequests = useCallback(async () => {
    if (!profile?.id) return;
    const data = await TrainerMemberService.getPendingRequests(profile.id);
    setPendingRequests(data);
  }, [profile?.id]);

  const handleAccept = async (request: PendingRequest) => {
    const relationshipId = request.id;
    const trainerId = request.trainer?.id;
    const trainerName = (
      displayNameFromProfile(request.trainer ?? undefined) ||
      request.trainer?.email ||
      'your trainer'
    ).trim();
    if (!profile?.id || !trainerId) return;
    try {
      setProcessingId(relationshipId);
      setError(null);
      setAcceptedTrainer(null);
      await TrainerMemberService.acceptRequest(relationshipId);
      void notifyTrainerOfInviteAccepted({ trainerId, relationshipId });
      // Insert system message in chat (member as sender so RLS allows: sender_id = auth.uid())
      await ChatService.insertSystemMessage(
        profile.id,
        trainerId,
        'You are now connected and can start discussing your goals. Just say hello!'
      );
      setAcceptedTrainer({ id: trainerId, name: trainerName });
      await loadPendingRequests();
    } catch (err: any) {
      console.error('Error accepting request:', err);
      setError(err.message || 'Failed to accept connection request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (relationshipId: string) => {
    if (!window.confirm('Are you sure you want to decline this connection request?')) {
      return;
    }

    try {
      setProcessingId(relationshipId);
      await TrainerMemberService.declineRequest(relationshipId);
      await loadPendingRequests(); // Reload the list
    } catch (err: any) {
      console.error('Error declining request:', err);
      alert(err.message || 'Failed to decline connection request');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /** High-level: "Today", "Yesterday", or short date like "Jan 17" */
  const formatNotificationDate = (dateString?: string) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const other = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.floor((today.getTime() - other.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    const sameYear = d.getFullYear() === today.getFullYear();
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
  };

  const handleNotificationClick = async (n: MemberNotification) => {
    if (!profile?.id) return;
    await markAsRead(profile.id, n.id);
    setTrainerUpdates((prev) =>
      prev.map((item) => (item.id === n.id ? { ...item, readAt: new Date().toISOString() } : item))
    );
    if (n.type === 'workout_removed') return;
    if (n.type === 'workouts_added' || n.type === 'workout_updated' || n.type === 'workout_rescheduled') {
      const sid = (n.workoutSessionId || '').trim();
      if (sid) navigate(`/workout-details/${sid}`);
    }
  };

  const renderNotificationItem = (n: MemberNotification) => {
    const isSessionNotification =
      n.type === 'workouts_added' ||
      n.type === 'workout_updated' ||
      n.type === 'workout_rescheduled' ||
      n.type === 'workout_removed';
    return (
      <button
        key={n.id}
        type="button"
        onClick={() => isSessionNotification && handleNotificationClick(n)}
        className={`w-full py-4 px-2 border-b border-theme-border dark:border-white/10 bg-transparent text-left ${isSessionNotification ? 'cursor-pointer hover:bg-theme-muted/20 dark:hover:bg-white/5' : 'cursor-default'}`}
      >
        <div className="flex items-center w-full">
          <p className={`text-base flex-1 min-w-0 break-words pr-4 ${n.readAt ? 'text-theme-text dark:text-theme-off-white font-normal' : 'text-theme-text dark:text-theme-off-white font-bold'}`}>
            {n.message}
            {formatNotificationDate(n.createdAt) && (
              <>
                {n.message.trim().endsWith('.') ? ' ' : '. '}
                <span className="text-theme-text/70 dark:text-theme-off-white/70 font-normal">
                  {formatNotificationDate(n.createdAt)}
                </span>
              </>
            )}
          </p>
          {!n.readAt && (
            <div className="w-10 shrink-0 flex items-center justify-center" aria-hidden>
              <span className="w-2.5 h-2.5 rounded-full bg-accent-blue ring-2 ring-theme-off-white dark:ring-theme-bg-dark" />
            </div>
          )}
        </div>
      </button>
    );
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="notifications-page min-h-screen flex flex-col bg-theme-off-white dark:bg-theme-bg-dark overflow-x-hidden pb-bottom-nav">
      {/* Header – no background */}
      <div className="notifications-page-header bg-transparent">
        <div className="px-4 h-16 flex items-center gap-3">
          <button
            onClick={() => navigate('/member-dashboard')}
            className="p-2 hover:bg-theme-muted dark:hover:bg-white/10 rounded-lg transition-colors text-theme-text dark:text-theme-off-white"
            aria-label="Back to dashboard"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <Bell className="w-6 h-6 text-theme-text dark:text-theme-off-white" />
          <h1 className="text-xl font-heading font-bold text-theme-text dark:text-theme-off-white uppercase">Notifications</h1>
        </div>
      </div>

      {/* Content – Chat-style: no white containers, sections with border-b */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 max-w-4xl mx-auto pb-8">
          {/* Error */}
          {error && (
            <div className="rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 px-4 py-3 flex items-center gap-2 text-red-800 dark:text-red-200 mt-4">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          {/* Success: accepted connection – CTA to open chat */}
          {acceptedTrainer && (
            <div className="rounded-2xl p-6 bg-status-completed/15 dark:bg-status-completed/10 border border-status-completed/30 dark:border-status-completed/20 mt-4">
              <p className="text-sm font-medium text-theme-text dark:text-theme-off-white mb-3">
                {CHAT_NAV_ENABLED
                  ? 'You can now have a chat with your trainer, go to messages.'
                  : 'You’re now connected with your trainer.'}
              </p>
              {CHAT_NAV_ENABLED ? (
              <button
                type="button"
                onClick={() => navigate('/chat', { state: { openUserId: acceptedTrainer.id } })}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-accent-blue text-white hover:opacity-90 transition-opacity shadow-sm"
              >
                <MessageCircle className="w-5 h-5" />
                Open messages
              </button>
              ) : null}
            </div>
          )}

          {/* Find a trainer slider – same as Chat */}
          <div className="pt-6 pb-6 border-b border-theme-border dark:border-white/10">
            <h2 className="text-lg sm:text-xl font-heading font-semibold text-theme-text dark:text-theme-off-white mb-4 uppercase">Find a trainer</h2>
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide scroll-smooth-touch -mx-4 pl-4 pr-4">
              {SLIDER_TRAINERS.map((t) => (
                <Link key={t.id} to="/personal-trainers" className="flex-shrink-0 flex flex-col items-center gap-1.5 min-w-[22.2vw]">
                  <div className="w-[min(21vw,5rem)] h-[min(21vw,5rem)] rounded-full flex items-center justify-center flex-shrink-0 [&>svg]:!w-[45%] [&>svg]:!h-[45%]" style={getAvatarStyle(String(t.id))}>
                    <UserIcon className="block" style={{ color: 'inherit' }} />
                  </div>
                  <span className="text-sm font-medium text-theme-text dark:text-theme-off-white truncate w-full text-center">{t.name.split(' ')[0]}</span>
                </Link>
              ))}
              <Link to="/personal-trainers" className="flex-shrink-0 flex flex-col items-center gap-1.5 min-w-[22.2vw]">
                <div className="w-[min(21vw,5rem)] h-[min(21vw,5rem)] rounded-full flex items-center justify-center border-2 border-dashed border-theme-border dark:border-white/50 bg-theme-muted/50 dark:bg-white/5 flex-shrink-0 [&>svg]:!w-[45%] [&>svg]:!h-[45%]">
                  <ChevronRight className="block text-theme-text/60 dark:text-white/80" />
                </div>
                <span className="text-sm text-theme-text/70 dark:text-theme-off-white font-medium">Browse</span>
              </Link>
            </div>
          </div>

          {/* 1) Connection requests – only when there are pending invitations */}
          {pendingRequests.length > 0 && (
            <div className="pt-6 pb-6 border-b border-theme-border dark:border-white/10">
              <h2 className="text-lg sm:text-xl font-heading font-semibold text-theme-text dark:text-theme-off-white mb-4 uppercase">
                Connection requests
              </h2>
              <div className="space-y-4">
                {pendingRequests.map((request) => {
                  const trainerName =
                    displayNameFromProfile(request.trainer ?? undefined) ||
                    request.trainer?.email?.split('@')[0] ||
                    'Unknown Trainer';
                  return (
                    <div
                      key={request.id}
                      className="rounded-2xl p-5 sm:p-6 bg-white dark:bg-white/10 border border-theme-border dark:border-white/10 shadow-sm dark:shadow-none flex items-center gap-4"
                    >
                      <div
                        className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center [&>svg]:w-8 [&>svg]:h-8 sm:[&>svg]:w-9 sm:[&>svg]:h-9"
                        style={getAvatarStyle(String(request.trainer?.id ?? request.id))}
                      >
                        <UserIcon className="block text-white" style={{ color: 'inherit' }} />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col gap-1">
                        <h3 className="text-lg sm:text-xl font-semibold text-theme-text dark:text-theme-off-white truncate uppercase">
                          {trainerName}
                        </h3>
                        <p className="text-sm text-theme-text/80 dark:text-theme-off-white/80">
                          You received a connecting request from a trainer
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <button
                            onClick={() => handleAccept(request)}
                            disabled={processingId === request.id}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-accent-blue rounded-xl hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <CheckCircle className="w-4 h-4 shrink-0" />
                            {processingId === request.id ? 'Processing...' : 'Accept'}
                          </button>
                          <button
                            onClick={() => handleDecline(request.id)}
                            disabled={processingId === request.id}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-theme-text dark:text-theme-off-white bg-white dark:bg-white/10 border border-theme-border dark:border-white/20 rounded-xl hover:bg-theme-muted/20 dark:hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-theme-border disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <XCircle className="w-4 h-4 shrink-0" />
                            Decline
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 2) Trainer updates – title "Notifications" only when there are connection requests */}
          <div className={pendingRequests.length > 0 ? 'pt-6 pb-8 mt-4' : 'pt-6 pb-8'}>
            {pendingRequests.length > 0 && (
              <h2 className="text-lg sm:text-xl font-heading font-semibold text-theme-text dark:text-theme-off-white mb-4 uppercase">
                Notifications
              </h2>
            )}
            {trainerUpdates.length === 0 ? (
              <div className="p-8 text-center text-theme-text/60 dark:text-theme-off-white/60">
                <p className="text-theme-text dark:text-theme-off-white">You have no notifications yet</p>
              </div>
            ) : (
              <>
                {last7Days.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-theme-text/80 dark:text-theme-off-white/80 mb-2 uppercase">
                      Last 7 days
                    </h3>
                    <div>
                      {last7Days.slice(0, last7DisplayCount).map((n) => renderNotificationItem(n))}
                    </div>
                    {last7Days.length > last7DisplayCount && (
                      <div className="mt-4 flex justify-center">
                        <button
                          type="button"
                          onClick={() => setLast7DisplayCount((prev) => prev + 5)}
                          className="rounded-full px-5 py-2.5 text-sm font-medium bg-white dark:bg-white/10 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/20 hover:bg-theme-muted/30 dark:hover:bg-white/20 transition-colors"
                        >
                          Show more
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {older.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-theme-text/80 dark:text-theme-off-white/80 mb-2 uppercase">
                      Older
                    </h3>
                    <div>
                      {older.slice(0, olderDisplayCount).map((n) => renderNotificationItem(n))}
                    </div>
                    {older.length > olderDisplayCount && (
                      <div className="mt-4 flex justify-center">
                        <button
                          type="button"
                          onClick={() => setOlderDisplayCount((prev) => prev + 5)}
                          className="rounded-full px-5 py-2.5 text-sm font-medium bg-white dark:bg-white/10 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/20 hover:bg-theme-muted/30 dark:hover:bg-white/20 transition-colors"
                        >
                          Show more
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

